import { AlertCircle, Check } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React from "react";

import { Label } from "./label";
import { cn } from "./utils";

export interface FormFieldProps {
  /** Field label text */
  label: string;
  /** Optional error message to display */
  error?: string | undefined;
  /** Whether the field has been validated successfully */
  valid?: boolean | undefined;
  /** HTML for attribute to associate label with input */
  htmlFor?: string;
  /** The form input element(s) */
  children: React.ReactNode;
  /** Additional class names for the wrapper */
  className?: string;
}

/**
 * FormField provides consistent error and success state styling for form inputs.
 * Includes animated error messages, shake animation on error, and success indicators.
 */
export function FormField({
  label,
  error,
  valid,
  htmlFor,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <Label htmlFor={htmlFor} className={cn(error && "text-destructive")}>
        {label}
      </Label>
      <div className={cn("relative", error && "animate-shake")}>
        {children}
        {valid && !error && (
          <Check
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-success"
            aria-hidden="true"
          />
        )}
      </div>
      <AnimatePresence mode="wait">
        {error && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="text-xs text-destructive flex items-center gap-1"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
